import * as Sentry from "@sentry/react";
import axios, { AxiosError } from "axios";
import { createRoot } from "react-dom/client";
import AppProviders from "./AppProviders.tsx";
import { useAuthStore } from "./hooks/AuthStore.ts";
import "./styles/index.css";

const API_URL = import.meta.env.VITE_API_URL;
const API_PORT = import.meta.env.VITE_API_PORT;

axios.defaults.baseURL = `${API_URL}:${API_PORT}`;
axios.defaults.withCredentials = true;
// Serialize array query params as repeated keys without brackets:
// ?status=VAL1&status=VAL2 instead of status[]=VAL1&status[]=VAL2
axios.defaults.paramsSerializer = {
  indexes: null,
};

// presigned s3 calls opt out of credentials, and adding a header there would
// have the bucket preflight a request its cors rule does not expect.
axios.interceptors.request.use((config) => {
  if (config.withCredentials) config.headers.set("X-CIFMD-Client", "web");

  return config;
});

// the screens take themselves back to sign-in once the session is gone, and
// they ask for their data before the application is there to listen
axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const { session, expire } = useAuthStore.getState();

    if (error.response?.status === 419 && session) expire();

    return Promise.reject(error);
  },
);

if (["production", "staging"].includes(import.meta.env.VITE_MODE)) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
    ],
    environment: import.meta.env.VITE_MODE,
    tracesSampleRate: 1.0,
    profilesSampleRate: 1.0,
  });
}

createRoot(document.getElementById("root")!).render(<AppProviders />);
